import React, { useState, useEffect, useRef } from 'react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import TreeNode from '@/components/TreeNode';
import SitemapControls from '@/components/SitemapControls';
import SitemapEditor from '@/components/SitemapEditor';
import MenuSelector from '@/components/MenuSelector';
import NodeCustomizer from '@/components/NodeCustomizer';
import SafeIcon from '@/common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { twMerge } from 'tailwind-merge';
import { generateShareUrl } from '@/utils/shareUtils';
import { isAirtableConfigured } from '@/services/airtable';
import { motion, AnimatePresence } from 'framer-motion';
import { toPng } from 'html-to-image';

// Recursive helper to update a node in the tree
const updateNodeInTree = (node, nodeId, updates) => {
  if (node.id === nodeId) {
    return { ...node, ...updates };
  }
  if (node.children) {
    return {
      ...node,
      children: node.children.map(child => updateNodeInTree(child, nodeId, updates))
    };
  }
  return node;
};

// Recursive helper to delete a node
const deleteNodeInTree = (node, nodeId) => {
  if (node.children) {
    const newChildren = node.children
      .filter(child => child.id !== nodeId)
      .map(child => deleteNodeInTree(child, nodeId));
    return { ...node, children: newChildren };
  }
  return node;
};

const Workspace = ({ workspaceData, isReadOnly, onCloudSave, onBack, cloudStatus }) => {
  // 🟢 ALWAYS Default to the first sitemap on mount
  const [activeMenuId, setActiveMenuId] = useState(workspaceData.menus[0]?.id);
  
  const [menus, setMenus] = useState(workspaceData.menus);
  const [projectTitle, setProjectTitle] = useState(workspaceData.title);
  const [theme, setTheme] = useState(workspaceData.theme || { 
    primary: '#6366f1', secondary: '#f8fafc', nodeBg: '#ffffff', lineColor: '#cbd5e1' 
  });
  
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isThemeOpen, setIsThemeOpen] = useState(false);
  const [layoutMode, setLayoutMode] = useState('vertical');
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [shareStatus, setShareStatus] = useState('idle');
  const [isCapturing, setIsCapturing] = useState(false);
  const [editingNode, setEditingNode] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const titleInputRef = useRef(null);
  const transformRef = useRef(null);
  const sitemapRef = useRef(null);

  // Sync props to state if workspace changes (e.g. cloud update)
  useEffect(() => {
    setMenus(workspaceData.menus);
    setProjectTitle(workspaceData.title);
    setTheme(workspaceData.theme || { primary: '#6366f1', secondary: '#f8fafc', nodeBg: '#ffffff', lineColor: '#cbd5e1' });
    
    // Safety check: Only reset activeMenuId if the current one is gone
    const currentStillExists = workspaceData.menus.find(m => m.id === activeMenuId);
    if (!currentStillExists && workspaceData.menus.length > 0) {
      setActiveMenuId(workspaceData.menus[0].id);
    }

    setHasUnsavedChanges(false);

    if (workspaceData.viewState && transformRef.current) {
      const { positionX, positionY, scale } = workspaceData.viewState;
      setTimeout(() => {
        if (transformRef.current) {
          transformRef.current.setTransform(positionX, positionY, scale);
        }
      }, 100);
    }
  }, [workspaceData]);

  // 🔄 Auto-Reset View when Menu or Layout Changes
  useEffect(() => {
    if (transformRef.current) {
       setTimeout(() => transformRef.current.resetTransform(), 50);
    }
  }, [layoutMode, activeMenuId]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges && !isReadOnly) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, isReadOnly]);

  const markDirty = () => {
    if (!isReadOnly) setHasUnsavedChanges(true);
  };

  const activeMenu = menus.find(m => m.id === activeMenuId) || menus[0];

  const handleSaveTree = (newTree) => {
    setMenus(prev => prev.map(menu => menu.id === activeMenuId ? { ...menu, tree: newTree } : menu));
    markDirty();
  };

  const handleNodeClick = (node) => {
    if (!isReadOnly) {
      setEditingNode(node);
    }
  };

  // 🎥 View Reset Handler (Passed to TreeNode)
  const handleNodeToggle = () => {
    if (transformRef.current) {
      setTimeout(() => {
        transformRef.current.resetTransform();
      }, 350);
    }
  };

  const handleNodeUpdate = (nodeId, updates) => {
    if (!activeMenu?.tree) return;
    const newTree = updateNodeInTree(activeMenu.tree, nodeId, updates);
    handleSaveTree(newTree);
  };

  const handleNodeDelete = (nodeId) => {
    if (!activeMenu?.tree) return;
    if (activeMenu.tree.id === nodeId) {
      alert("Cannot delete the root node.");
      return;
    }
    const newTree = deleteNodeInTree(activeMenu.tree, nodeId);
    handleSaveTree(newTree);
  };

  const handleManualSaveClick = async () => {
    if (onCloudSave) {
      let viewState = null;
      if (transformRef.current) {
        const { positionX, positionY, scale } = transformRef.current.instance.transformState;
        viewState = { positionX, positionY, scale };
      }
      await onCloudSave(workspaceData.id, { 
        title: projectTitle, 
        menus: menus, 
        activeId: activeMenuId, 
        theme: theme, 
        viewState: viewState 
      });
      setHasUnsavedChanges(false);
    }
  };

  const handleBackClick = () => {
    if (hasUnsavedChanges) {
      if (window.confirm("You have unsaved changes. Are you sure you want to leave?")) {
        onBack();
      }
    } else {
      onBack();
    }
  };

  const handleAddMenu = () => {
    const newId = `menu-${Date.now()}`;
    const newMenu = {
      id: newId,
      title: 'New Sitemap',
      tree: { id: `root-${Date.now()}`, label: 'Home', type: 'root', children: [] }
    };
    setMenus([...menus, newMenu]);
    setActiveMenuId(newId);
    markDirty();
  };

  const handleDeleteMenu = (id) => {
    if (menus.length <= 1) return;
    const newMenus = menus.filter(m => m.id !== id);
    setMenus(newMenus);
    if (activeMenuId === id) {
      setActiveMenuId(newMenus[0].id);
    }
    markDirty();
  };

  const handleRenameMenu = (id, newTitle) => {
    setMenus(prev => prev.map(menu => menu.id === id ? { ...menu, title: newTitle } : menu));
    markDirty();
  };

  const handleShare = async () => {
    if (hasUnsavedChanges) {
      if(confirm("Project must be saved before sharing. Save now?")) {
        await handleManualSaveClick();
      } else {
        return;
      }
    }
    setShareStatus('loading');
    let viewState = null;
    if (transformRef.current) {
      const { positionX, positionY, scale } = transformRef.current.instance.transformState;
      viewState = { positionX, positionY, scale };
    }
    const dataToShare = {
      id: workspaceData.id,
      title: projectTitle,
      menus: menus,
      activeId: activeMenuId,
      theme: theme,
      viewState: viewState
    };
    
    let shareUrl = '';
    if (isAirtableConfigured()) {
       try {
         const url = new URL(window.location.href);
         url.searchParams.set('id', workspaceData.id);
         url.searchParams.delete('s');
         shareUrl = url.toString();
       } catch (e) {}
    } 
    if (!shareUrl) {
      shareUrl = generateShareUrl(dataToShare);
    }

    if (shareUrl) {
      navigator.clipboard.writeText(shareUrl);
      setShareStatus('success');
      setTimeout(() => setShareStatus('idle'), 2500);
    } else {
      setShareStatus('error');
      setTimeout(() => setShareStatus('idle'), 2500);
    }
  };

  const handleScreenshot = async () => {
    if (!sitemapRef.current) return;
    setIsCapturing(true);
    
    try {
      // Capture the sitemap container
      // pixelRatio: 2 ensures high resolution (Retina quality)
      const dataUrl = await toPng(sitemapRef.current, {
        backgroundColor: theme.secondary,
        pixelRatio: 2,
        style: {
          transform: 'none', // Reset any potential transforms on the container itself
        }
      });
      
      const link = document.createElement('a');
      link.download = `${projectTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'sitemap'}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error('Screenshot failed', err);
      alert('Failed to generate image. Please try again.');
    } finally {
      setIsCapturing(false);
    }
  };

  const handleThemeUpdate = (key, value) => {
    setTheme(prev => ({ ...prev, [key]: value }));
    markDirty();
  };

  return (
    <div 
      className="fixed inset-0 w-full h-full overflow-hidden font-sans selection:bg-indigo-100 z-0 transition-colors duration-500"
      style={{ backgroundColor: theme.secondary }}
    >
      {/* Background */}
      <div 
        className="absolute inset-0 z-0 pointer-events-none opacity-[0.03] print:hidden" 
        style={{
          backgroundImage: `linear-gradient(${theme.primary} 1px, transparent 1px), linear-gradient(90deg, ${theme.primary} 1px, transparent 1px)`,
          backgroundSize: '40px 40px'
        }} 
      />

      {/* Header - Hidden on Print */}
      <div className="fixed top-0 left-0 w-full p-6 z-50 pointer-events-none print:hidden">
        <div className="flex justify-between items-start pointer-events-auto max-w-7xl mx-auto">
          {/* Title Card */}
          <div className="bg-white/80 backdrop-blur-xl p-5 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-white/50 max-w-md ring-1 ring-white/60 min-w-[340px]">
             {/* ... Header Content ... */}
             <div className="flex items-center gap-3 mb-4">
              {!isReadOnly && (
                <button onClick={handleBackClick} className="mr-1 p-2 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-700 transition-colors">
                  <SafeIcon icon={FiIcons.FiArrowLeft} />
                </button>
              )}
              <div 
                className="p-2.5 rounded-xl border shadow-sm transition-colors"
                style={{ 
                    backgroundColor: isReadOnly ? '#fffbeb' : `${theme.primary}10`,
                    color: isReadOnly ? '#d97706' : theme.primary,
                    borderColor: isReadOnly ? '#fcd34d' : `${theme.primary}30`
                }}
              >
                <SafeIcon icon={isReadOnly ? FiIcons.FiLock : FiIcons.FiMap} className="text-xl" />
              </div>
              <div className="flex-1 overflow-hidden">
                {isEditingTitle && !isReadOnly ? (
                  <form onSubmit={(e) => { e.preventDefault(); setIsEditingTitle(false); }}>
                    <input 
                      ref={titleInputRef} 
                      autoFocus 
                      type="text" 
                      value={projectTitle} 
                      onChange={(e) => { setProjectTitle(e.target.value); markDirty(); }} 
                      onBlur={() => setIsEditingTitle(false)}
                      className="text-xl font-bold text-slate-800 leading-none tracking-tight w-full bg-slate-100 px-2 py-1 rounded border border-indigo-200 focus:outline-none"
                    />
                  </form>
                ) : (
                  <div className="flex items-center gap-2">
                    <h1 
                      onClick={() => !isReadOnly && setIsEditingTitle(true)}
                      className={`text-xl font-bold text-slate-800 leading-none tracking-tight rounded px-1 -ml-1 truncate ${!isReadOnly ? 'hover:text-indigo-600 cursor-text hover:bg-slate-50/80 group' : ''}`}
                    >
                      {projectTitle}
                      {!isReadOnly && <SafeIcon icon={FiIcons.FiEdit2} className="inline ml-2 text-xs opacity-0 group-hover:opacity-100 text-slate-400" />}
                    </h1>
                    {isReadOnly && (
                      <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] uppercase font-bold rounded-full border border-amber-200">
                        Read Only
                      </span>
                    )}
                  </div>
                )}
                 <div className="flex items-center gap-2 mt-1">
                  <p className="text-xs text-slate-500 font-medium px-1">Interactive Flowchart Suite</p>
                  {!isReadOnly && (
                    <div className="flex items-center gap-1.5 ml-2">
                       {hasUnsavedChanges ? (
                         <span className="text-[10px] font-bold text-amber-500 uppercase tracking-wide flex items-center gap-1">
                           <span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> Unsaved
                         </span>
                       ) : (
                         <span className="text-[10px] font-bold text-slate-300 uppercase tracking-wide">Saved</span>
                       )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex gap-2 items-center">
              <MenuSelector 
                menus={menus} 
                activeMenuId={activeMenuId} 
                onSelect={setActiveMenuId} 
                onAdd={handleAddMenu} 
                onDelete={handleDeleteMenu} 
                onRename={handleRenameMenu} 
                readOnly={isReadOnly}
              />
              {!isReadOnly && (
                <>
                  <button onClick={() => setIsEditorOpen(true)} className="flex-1 py-1.5 px-3 bg-slate-800 hover:bg-slate-700 text-white rounded-lg text-sm font-medium transition-all shadow-lg shadow-slate-200 hover:shadow-xl active:scale-95 flex items-center justify-center gap-2">
                    <SafeIcon icon={FiIcons.FiEdit3} /> Edit
                  </button>
                  <button onClick={() => setIsThemeOpen(true)} className="p-2 bg-white border border-slate-200 text-slate-600 hover:text-indigo-600 rounded-lg shadow-sm transition-all">
                    <SafeIcon icon={FiIcons.FiDroplet} />
                  </button>
                </>
              )}
              <div className="bg-slate-100 p-1 rounded-lg flex shrink-0">
                <button onClick={() => setLayoutMode('vertical')} className={twMerge("p-1.5 rounded-md transition-all", layoutMode === 'vertical' ? "bg-white text-indigo-600 shadow-sm" : "text-slate-400 hover:text-slate-600")}>
                  <SafeIcon icon={FiIcons.FiGitMerge} className="rotate-180 text-lg" />
                </button>
                <button onClick={() => setLayoutMode('horizontal')} className={twMerge("p-1.5 rounded-md transition-all", layoutMode === 'horizontal' ? "bg-white text-indigo-600 shadow-sm" : "text-slate-400 hover:text-slate-600")}>
                  <SafeIcon icon={FiIcons.FiGitMerge} className="rotate-90 text-lg" />
                </button>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3 items-end">
            <div className="bg-white/80 backdrop-blur-xl p-2 rounded-2xl shadow-sm border border-white/50 flex gap-2 ring-1 ring-white/60">
              {!isReadOnly && (
                <button onClick={handleManualSaveClick} disabled={cloudStatus === 'saving' || !hasUnsavedChanges} className={`px-3 py-2 rounded-xl transition-all font-medium flex items-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed ${hasUnsavedChanges ? 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-md shadow-indigo-200' : 'bg-slate-100 text-slate-400'}`}>
                  {cloudStatus === 'saving' ? <SafeIcon icon={FiIcons.FiLoader} className="animate-spin" /> : <SafeIcon icon={FiIcons.FiSave} />} Save
                </button>
              )}
              {!isReadOnly && <div className="w-px bg-slate-200 my-1 mx-1"></div>}
              
              <button 
                onClick={handleScreenshot}
                disabled={isCapturing}
                className="p-2 text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all relative group disabled:opacity-50 disabled:cursor-wait"
                title="Download Image"
              >
                {isCapturing ? (
                  <SafeIcon icon={FiIcons.FiLoader} className="text-xl animate-spin text-indigo-600" />
                ) : (
                  <SafeIcon icon={FiIcons.FiCamera} className="text-xl" />
                )}
              </button>

              <button 
                onClick={handleShare}
                disabled={shareStatus === 'loading'}
                className="p-2 text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all relative group disabled:opacity-50"
                title="Share Project"
              >
                {shareStatus === 'loading' ? <SafeIcon icon={FiIcons.FiLoader} className="text-xl animate-spin" /> : <SafeIcon icon={FiIcons.FiShare2} className="text-xl" />}
                <AnimatePresence>
                  {shareStatus === 'success' && <motion.div initial={{opacity:0,y:10}} animate={{opacity:1,y:0}} exit={{opacity:0}} className="absolute top-full right-0 mt-2 px-3 py-1 bg-green-600 text-white text-xs rounded-lg shadow-lg whitespace-nowrap z-[100] pointer-events-none">Copied URL!</motion.div>}
                  {shareStatus === 'error' && <motion.div initial={{opacity:0,y:10}} animate={{opacity:1,y:0}} exit={{opacity:0}} className="absolute top-full right-0 mt-2 px-3 py-1 bg-red-600 text-white text-xs rounded-lg shadow-lg whitespace-nowrap z-[100] pointer-events-none">Failed to share</motion.div>}
                </AnimatePresence>
              </button>
            </div>
          </div>
        </div>
      </div>

      {!isReadOnly && (
        <>
          <SitemapEditor isOpen={isEditorOpen} onClose={() => setIsEditorOpen(false)} currentData={activeMenu.tree} menuTitle={activeMenu.title} onSave={handleSaveTree} />
          <NodeCustomizer isOpen={!!editingNode} node={editingNode} onClose={() => setEditingNode(null)} onUpdate={handleNodeUpdate} onDelete={handleNodeDelete} />
          
          <AnimatePresence>
            {isThemeOpen && (
              <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 print:hidden">
                <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} onClick={() => setIsThemeOpen(false)} className="absolute inset-0 bg-slate-900/20 backdrop-blur-sm" />
                <motion.div initial={{opacity:0, scale:0.95}} animate={{opacity:1, scale:1}} exit={{opacity:0, scale:0.95}} className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden">
                  <div className="px-5 py-3 border-b border-slate-100 flex justify-between items-center">
                    <h3 className="font-bold text-slate-700">Theme Settings</h3>
                    <button onClick={() => setIsThemeOpen(false)}><SafeIcon icon={FiIcons.FiX} className="text-slate-400 hover:text-slate-600" /></button>
                  </div>
                  <div className="p-5 space-y-4">
                    <div>
                      <label className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-2">Primary Color</label>
                      <div className="flex gap-2">
                        <input type="color" value={theme.primary} onChange={(e) => handleThemeUpdate('primary', e.target.value)} className="w-10 h-10 rounded-lg cursor-pointer border-0 p-0" />
                        <input type="text" value={theme.primary} onChange={(e) => handleThemeUpdate('primary', e.target.value)} className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm" />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-2">Background Color</label>
                      <div className="flex gap-2">
                        <input type="color" value={theme.secondary} onChange={(e) => handleThemeUpdate('secondary', e.target.value)} className="w-10 h-10 rounded-lg cursor-pointer border-0 p-0" />
                        <input type="text" value={theme.secondary} onChange={(e) => handleThemeUpdate('secondary', e.target.value)} className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm" />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-2">Line Color</label>
                      <div className="flex gap-2">
                        <input type="color" value={theme.lineColor} onChange={(e) => handleThemeUpdate('lineColor', e.target.value)} className="w-10 h-10 rounded-lg cursor-pointer border-0 p-0" />
                        <input type="text" value={theme.lineColor} onChange={(e) => handleThemeUpdate('lineColor', e.target.value)} className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm" />
                      </div>
                    </div>
                     <div>
                      <label className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-2">Node Background</label>
                      <div className="flex gap-2">
                        <input type="color" value={theme.nodeBg} onChange={(e) => handleThemeUpdate('nodeBg', e.target.value)} className="w-10 h-10 rounded-lg cursor-pointer border-0 p-0" />
                        <input type="text" value={theme.nodeBg} onChange={(e) => handleThemeUpdate('nodeBg', e.target.value)} className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm" />
                      </div>
                    </div>
                  </div>
                  <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 text-right">
                    <button onClick={() => setIsThemeOpen(false)} className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium">Done</button>
                  </div>
                </motion.div>
              </div>
            )}
          </AnimatePresence>
        </>
      )}

      {/* Canvas */}
      <TransformWrapper 
        ref={transformRef}
        key={`${activeMenuId}-${isReadOnly}`}
        minScale={0.1} 
        maxScale={2.5} 
        wheel={{ step: 0.1 }}
        limitToBounds={false}
        centerOnInit={true}
      >
        {({ zoomIn, zoomOut, resetTransform }) => (
          <>
            <div className="print:hidden">
              <SitemapControls 
                onScreenshot={handleScreenshot} 
                isCapturing={isCapturing} 
              />
            </div>
            <TransformComponent 
              wrapperStyle={{ width: '100vw', height: '100vh', position: 'fixed', top: 0, left: 0, zIndex: 0 }}
              contentStyle={{ width: 'fit-content', height: 'fit-content', minWidth: '100vw', minHeight: '100vh', display: 'flex' }}
              wrapperClass="print-wrapper"
              contentClass="print-content"
            >
              <div 
                ref={sitemapRef}
                className={twMerge("m-auto p-20", layoutMode === 'vertical' ? "tree-vertical" : "tree-horizontal")}
                style={{ '--line-color': theme.lineColor }}
              >
                {activeMenu && activeMenu.tree && (
                  <ul className="tree-ul" key={`${activeMenu.id}-${layoutMode}`}>
                    <TreeNode 
                      node={activeMenu.tree} 
                      isRoot={true} 
                      layoutMode={layoutMode} 
                      onNodeClick={handleNodeClick}
                      onToggle={handleNodeToggle} 
                      theme={theme}
                    />
                  </ul>
                )}
              </div>
            </TransformComponent>
          </>
        )}
      </TransformWrapper>
    </div>
  );
};

export default Workspace;